import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ClipboardList, Plus, RefreshCw, Search, Printer, Eye, X, Loader2,
  CheckCircle2, AlertCircle, ArrowLeft, Lock, Download,
} from "lucide-react";
import { PageHeader } from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import ContainerDiagram from "../components/ContainerDiagram";
import ContainerNumberScanner from "../components/ContainerNumberScanner";
import { downloadEirPdf, downloadCsv } from "../lib/exporters";

const ISO_TYPES = ["22G1", "22U1", "42G1", "45G1", "45R1", "22R1", "22T6", "22T1", "25T0", "22T4", "26T6", "L4BN"];

function fmtTime(t) {
  if (!t) return "—";
  try { return new Date(t).toLocaleString(); } catch { return t; }
}

function StatusPill({ status }) {
  return status === "OPEN"
    ? <span className="ps-pill ps-pill-warning" data-testid={`status-${status}`}><span className="dot" />OPEN</span>
    : <span className="ps-pill ps-pill-success" data-testid={`status-${status}`}><span className="dot" />CLOSED</span>;
}

// --- Create EIR dialog ---
function CreateEirDialog({ open, onClose, onCreated }) {
  const [partners, setPartners] = useState([]);
  useEffect(() => {
    if (!open) return;
    api.get("/partners?active=true&role=carrier")
      .then((r) => setPartners(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => {});
  }, [open]);
  const todayLocal = () => {
    const d = new Date();
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  };
  const empty = () => ({
    container_number: "",
    container_iso_type: "22G1",
    carrier_code: "",
    customer: "",
    depot_code: "GHTEM",
    depot_name: "Multi Purpose Terminal Ghana (MTG)",
    gate_in: {
      event_time: todayLocal(),
      driver_name: "",
      truck_plate: "",
      seal_number: "",
      grade: "B",
      condition: "SOUND",
      surveyor_name: "",
      damages: [],
      remarks: "",
      tank_inspection: null,
    },
  });
  const [form, setForm] = useState(empty());
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { if (open) { setForm(empty()); setErr(""); } }, [open]);

  if (!open) return null;

  // Detect tank by ISO code (T-types: 22T6, 25T0, 22T1, etc — letter T at position 3)
  const isTank = /T\d?$|^.{2}T/i.test(form.container_iso_type || "");

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setGI = (k, v) => setForm((f) => ({ ...f, gate_in: { ...f.gate_in, [k]: v } }));
  const setTI = (k, v) => setForm((f) => ({
    ...f,
    gate_in: {
      ...f.gate_in,
      tank_inspection: { ...(f.gate_in.tank_inspection || {}), [k]: v },
    },
  }));

  const submit = async (e) => {
    e?.preventDefault?.();
    setErr("");
    if (!/^[A-Z]{3,4}\d{6,7}$/i.test(form.container_number.trim())) {
      setErr("Container number must follow ISO format, e.g. MSCU1234567");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        container_number: form.container_number.toUpperCase().trim(),
        carrier_code: form.carrier_code.toUpperCase().trim() || null,
        gate_in: {
          ...form.gate_in,
          event_time: new Date(form.gate_in.event_time).toISOString(),
        },
      };
      const { data } = await api.post("/eirs", payload);
      toast.success(`EIR ${data.eir_no} created`);
      if (data._auto_wash) {
        if (data._auto_wash.action === "created") {
          toast.success(`Auto-created wash order ${data._auto_wash.wash_no}`);
        } else if (data._auto_wash.action === "exists") {
          toast.info(`Existing wash order ${data._auto_wash.wash_no} found for this tank`);
        }
      }
      onCreated?.(data);
      onClose?.();
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="create-eir-dialog">
      <div className="bg-white max-w-5xl w-full mt-8 rounded-sm shadow-xl">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Gate In · New EIR</div>
            <h2 className="font-display text-xl font-extrabold">Issue Equipment Interchange Receipt</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-900" data-testid="close-create-eir">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-5">
          {err && (
            <div className="border border-red-200 bg-red-50 text-red-900 text-sm p-3 flex items-start gap-2" data-testid="eir-create-error">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><div>{err}</div>
            </div>
          )}
          {/* Container & carrier */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Container *</label>
              <div className="flex gap-2">
                <input value={form.container_number} onChange={(e) => setF("container_number", e.target.value.toUpperCase())} className="ps-input font-mono flex-1" placeholder="MSCU1234567" data-testid="eir-container" required />
                <ContainerNumberScanner
                  dataTestid="eir-ocr"
                  small
                  onResult={(cn) => setF("container_number", cn)}
                />
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">ISO type</label>
              <select value={form.container_iso_type} onChange={(e) => setF("container_iso_type", e.target.value)} className="ps-input font-mono" data-testid="eir-iso">
                {ISO_TYPES.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Carrier code</label>
              <select
                value={form.carrier_code}
                onChange={(e) => {
                  const code = e.target.value;
                  const partner = partners.find((p) => p.code === code);
                  setF("carrier_code", code);
                  // Auto-fill customer with carrier's legal name when customer is empty
                  if (partner && !form.customer) setF("customer", partner.legal_name || code);
                }}
                className="ps-input font-mono"
                data-testid="eir-carrier"
              >
                <option value="">— Select carrier —</option>
                {partners.map((p) => (
                  <option key={p.code} value={p.code}>
                    {p.code}{p.legal_name && p.legal_name !== p.code ? ` · ${p.legal_name}` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Customer / Principal</label>
              <input value={form.customer} onChange={(e) => setF("customer", e.target.value)} className="ps-input" data-testid="eir-customer" placeholder="Defaults to carrier legal name" />
            </div>
          </div>

          {/* Gate IN details */}
          <div className="border border-gray-200 bg-gray-50 p-4 space-y-3">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-gray-600">Gate IN details</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Date / time *</label>
                <input type="datetime-local" value={form.gate_in.event_time} onChange={(e) => setGI("event_time", e.target.value)} className="ps-input font-mono" required data-testid="eir-gi-time" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Driver</label>
                <input value={form.gate_in.driver_name} onChange={(e) => setGI("driver_name", e.target.value)} className="ps-input" data-testid="eir-gi-driver" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Truck plate</label>
                <input value={form.gate_in.truck_plate} onChange={(e) => setGI("truck_plate", e.target.value.toUpperCase())} className="ps-input font-mono" data-testid="eir-gi-truck" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Seal #</label>
                <input value={form.gate_in.seal_number} onChange={(e) => setGI("seal_number", e.target.value.toUpperCase())} className="ps-input font-mono" data-testid="eir-gi-seal" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Grade</label>
                <select value={form.gate_in.grade} onChange={(e) => setGI("grade", e.target.value)} className="ps-input font-mono" data-testid="eir-gi-grade">
                  <option value="A">A</option><option value="B">B</option><option value="C">C</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Condition</label>
                <select value={form.gate_in.condition} onChange={(e) => setGI("condition", e.target.value)} className="ps-input font-mono" data-testid="eir-gi-condition">
                  <option value="SOUND">Sound</option>
                  <option value="DAMAGED">Damaged</option>
                  <option value="UNDER_SURVEY">Under Survey</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Surveyor</label>
                <input value={form.gate_in.surveyor_name} onChange={(e) => setGI("surveyor_name", e.target.value)} className="ps-input" data-testid="eir-gi-surveyor" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Remarks</label>
                <input value={form.gate_in.remarks} onChange={(e) => setGI("remarks", e.target.value)} className="ps-input" data-testid="eir-gi-remarks" />
              </div>
            </div>

            {/* Damage diagram */}
            <div className="pt-2">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-gray-600 mb-2">
                Container damage diagram — click any panel to mark damage
              </div>
              <ContainerDiagram damages={form.gate_in.damages} onChange={(d) => setGI("damages", d)} />
            </div>

            {/* Tank interior inspection — only for ISO tank containers */}
            {isTank && (
              <div className="pt-4 border-t border-dashed border-amber-300" data-testid="tank-inspection-panel">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-amber-700">
                    ISO Tank · Interior inspection
                  </div>
                  <div className="text-xs text-gray-500">Detected ISO {form.container_iso_type}</div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Interior clean?</label>
                    <select
                      value={String(form.gate_in.tank_inspection?.interior_clean ?? "")}
                      onChange={(e) => setTI("interior_clean", e.target.value === "" ? null : e.target.value === "true")}
                      className="ps-input"
                      data-testid="ti-interior-clean"
                    >
                      <option value="">—</option>
                      <option value="true">Yes — clean</option>
                      <option value="false">No — needs wash</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Residue present?</label>
                    <select
                      value={String(form.gate_in.tank_inspection?.residue_present ?? "")}
                      onChange={(e) => setTI("residue_present", e.target.value === "" ? null : e.target.value === "true")}
                      className="ps-input"
                      data-testid="ti-residue"
                    >
                      <option value="">—</option>
                      <option value="false">No</option>
                      <option value="true">Yes</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Residue type</label>
                    <input
                      placeholder="e.g. Acetone"
                      value={form.gate_in.tank_inspection?.residue_type || ""}
                      onChange={(e) => setTI("residue_type", e.target.value)}
                      className="ps-input"
                      disabled={!form.gate_in.tank_inspection?.residue_present}
                      data-testid="ti-residue-type"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Residue amount</label>
                    <select
                      value={form.gate_in.tank_inspection?.residue_amount || ""}
                      onChange={(e) => setTI("residue_amount", e.target.value || null)}
                      className="ps-input font-mono"
                      disabled={!form.gate_in.tank_inspection?.residue_present}
                      data-testid="ti-residue-amount"
                    >
                      <option value="">—</option>
                      <option value="LIGHT">Light</option>
                      <option value="MODERATE">Moderate</option>
                      <option value="HEAVY">Heavy</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Last cargo</label>
                    <input
                      value={form.gate_in.tank_inspection?.last_cargo || ""}
                      onChange={(e) => setTI("last_cargo", e.target.value)}
                      className="ps-input"
                      data-testid="ti-last-cargo"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Last cargo UN #</label>
                    <input
                      value={form.gate_in.tank_inspection?.last_cargo_un_no || ""}
                      onChange={(e) => setTI("last_cargo_un_no", e.target.value)}
                      className="ps-input font-mono"
                      data-testid="ti-un"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Interior damaged?</label>
                    <select
                      value={String(form.gate_in.tank_inspection?.interior_damaged ?? "")}
                      onChange={(e) => setTI("interior_damaged", e.target.value === "" ? null : e.target.value === "true")}
                      className="ps-input"
                      data-testid="ti-int-damaged"
                    >
                      <option value="">—</option>
                      <option value="false">No</option>
                      <option value="true">Yes</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Damage notes</label>
                    <input
                      value={form.gate_in.tank_inspection?.interior_damage_notes || ""}
                      onChange={(e) => setTI("interior_damage_notes", e.target.value)}
                      className="ps-input"
                      disabled={!form.gate_in.tank_inspection?.interior_damaged}
                      data-testid="ti-damage-notes"
                    />
                  </div>
                </div>

                <div className="mt-4 flex items-center gap-3 bg-amber-50 border border-amber-200 p-3">
                  <input
                    type="checkbox"
                    id="needs-wash"
                    checked={!!form.gate_in.tank_inspection?.needs_washing}
                    onChange={(e) => setTI("needs_washing", e.target.checked)}
                    className="w-5 h-5"
                    data-testid="ti-needs-wash"
                  />
                  <label htmlFor="needs-wash" className="flex-1 cursor-pointer">
                    <div className="text-sm font-bold">Auto-create a wash order from this gate-in</div>
                    <div className="text-xs text-gray-600">
                      A REQUESTED wash order will be created immediately, pre-filled with the cargo info above. You can complete customer details and procedures in the Wash Station.
                    </div>
                  </label>
                  {form.gate_in.tank_inspection?.needs_washing && (
                    <input
                      placeholder="Wash customer (optional)"
                      value={form.gate_in.tank_inspection?.wash_customer || ""}
                      onChange={(e) => setTI("wash_customer", e.target.value)}
                      className="ps-input max-w-[200px]"
                      data-testid="ti-wash-customer"
                    />
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-2">
            <button type="button" onClick={onClose} className="ps-btn ps-btn-secondary" data-testid="cancel-create-eir">Cancel</button>
            <button type="submit" disabled={submitting} className="ps-btn ps-btn-primary" data-testid="submit-create-eir">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating…</> : <><CheckCircle2 className="w-4 h-4" /> Issue EIR</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// --- Close EIR dialog ---
function CloseEirDialog({ eir, onClose, onClosed }) {
  const todayLocal = () => {
    const d = new Date();
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  };
  const [form, setForm] = useState({
    event_time: todayLocal(),
    driver_name: "",
    truck_plate: "",
    seal_number: "",
    condition: eir?.gate_in?.condition || "SOUND",
    released_to: eir?.customer || "",
    remarks: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  if (!eir) return null;

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      const payload = { gate_out: { ...form, event_time: new Date(form.event_time).toISOString() } };
      const { data } = await api.post(`/eirs/${eir.id}/close`, payload);
      toast.success(`EIR ${data.eir_no} closed`);
      onClosed?.(data);
      onClose?.();
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="close-eir-dialog">
      <div className="bg-white max-w-2xl w-full mt-16 rounded-sm shadow-xl">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Gate Out · {eir.eir_no}</div>
            <h2 className="font-display text-xl font-extrabold">Close EIR — release container</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-900" data-testid="close-close-dialog">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          {err && (
            <div className="border border-red-200 bg-red-50 text-red-900 text-sm p-3 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><div>{err}</div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Container</label>
              <input readOnly value={eir.container_number} className="ps-input font-mono bg-gray-50" />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Gate-out time *</label>
              <input type="datetime-local" required value={form.event_time} onChange={(e) => setF("event_time", e.target.value)} className="ps-input font-mono" data-testid="eir-go-time" />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Driver *</label>
              <input required value={form.driver_name} onChange={(e) => setF("driver_name", e.target.value)} className="ps-input" data-testid="eir-go-driver" />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Truck plate *</label>
              <input required value={form.truck_plate} onChange={(e) => setF("truck_plate", e.target.value.toUpperCase())} className="ps-input font-mono" data-testid="eir-go-truck" />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Seal # (out)</label>
              <input value={form.seal_number} onChange={(e) => setF("seal_number", e.target.value.toUpperCase())} className="ps-input font-mono" data-testid="eir-go-seal" />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Condition at out</label>
              <select value={form.condition} onChange={(e) => setF("condition", e.target.value)} className="ps-input font-mono" data-testid="eir-go-condition">
                <option value="SOUND">Sound</option>
                <option value="DAMAGED">Damaged</option>
                <option value="UNDER_SURVEY">Under Survey</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Released to</label>
              <input value={form.released_to} onChange={(e) => setF("released_to", e.target.value)} className="ps-input" data-testid="eir-go-released-to" />
            </div>
            <div className="col-span-2">
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Remarks</label>
              <input value={form.remarks} onChange={(e) => setF("remarks", e.target.value)} className="ps-input" data-testid="eir-go-remarks" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="ps-btn ps-btn-secondary">Cancel</button>
            <button type="submit" disabled={submitting} className="ps-btn ps-btn-primary" data-testid="submit-close-eir">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Closing…</> : <><Lock className="w-4 h-4" /> Close EIR</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// --- Detail view ---
function EirDetail({ eir, onBack, onClose }) {
  return (
    <div className="space-y-4" data-testid="eir-detail">
      <div className="flex items-center justify-between gap-2">
        <button className="ps-btn ps-btn-secondary" onClick={onBack} data-testid="eir-back">
          <ArrowLeft className="w-4 h-4" /> Back to list
        </button>
        <div className="flex gap-2">
          <button className="ps-btn ps-btn-primary" onClick={() => downloadEirPdf(eir)} data-testid="eir-print">
            <Printer className="w-4 h-4" /> Print EIR (PDF)
          </button>
          {eir.status === "OPEN" && (
            <button className="ps-btn ps-btn-secondary" onClick={onClose} data-testid="eir-close-btn">
              <Lock className="w-4 h-4" /> Close (Gate Out)
            </button>
          )}
        </div>
      </div>

      <div className="border border-gray-200 bg-white">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">EIR</div>
            <div className="font-mono font-bold text-lg">{eir.eir_no}</div>
            <div className="mt-1"><StatusPill status={eir.status} /></div>
          </div>
          <div className="text-right text-sm">
            <div className="font-mono font-bold">{eir.container_number}</div>
            <div className="text-gray-500 font-mono text-xs">{eir.container_iso_type}</div>
            {eir.carrier_code && <div className="text-xs text-gray-500">Carrier {eir.carrier_code}</div>}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 divide-x divide-gray-200">
          <div className="p-5">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#006E9E] mb-3">Gate IN</div>
            <KvList rows={[
              ["Date / time", fmtTime(eir.gate_in?.event_time)],
              ["Driver", eir.gate_in?.driver_name],
              ["Truck plate", eir.gate_in?.truck_plate],
              ["Seal #", eir.gate_in?.seal_number],
              ["Grade", eir.gate_in?.grade],
              ["Condition", eir.gate_in?.condition],
              ["Surveyor", eir.gate_in?.surveyor_name],
              ["Remarks", eir.gate_in?.remarks],
            ]} />
          </div>
          <div className="p-5">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#006E9E] mb-3">Gate OUT</div>
            {eir.gate_out ? (
              <KvList rows={[
                ["Date / time", fmtTime(eir.gate_out.event_time)],
                ["Driver", eir.gate_out.driver_name],
                ["Truck plate", eir.gate_out.truck_plate],
                ["Seal #", eir.gate_out.seal_number],
                ["Condition", eir.gate_out.condition],
                ["Released to", eir.gate_out.released_to],
                ["Remarks", eir.gate_out.remarks],
                ["Closed at", fmtTime(eir.closed_at)],
              ]} />
            ) : (
              <div className="text-gray-400 text-sm">Not yet released. Open status.</div>
            )}
          </div>
        </div>
      </div>

      <div className="border border-gray-200 bg-white p-4">
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-gray-600 mb-3">Damages on gate-in</div>
        <ContainerDiagram damages={eir.gate_in?.damages || []} readOnly onChange={() => {}} />
      </div>
    </div>
  );
}

function KvList({ rows }) {
  return (
    <dl className="space-y-2 text-sm">
      {rows.map(([k, v]) => (
        <div key={k} className="grid grid-cols-3 gap-2">
          <dt className="text-[10px] font-bold uppercase tracking-[0.16em] text-gray-500 self-center">{k}</dt>
          <dd className="col-span-2 font-mono">{v || <span className="text-gray-400">—</span>}</dd>
        </div>
      ))}
    </dl>
  );
}

// --- Main page ---
export default function EIRs() {
  const { user } = useAuth();
  const canCreate = !!user;
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: "",
    container_number: (searchParams.get("container") || "").toUpperCase(),
    carrier_code: "",
  });
  const [carriers, setCarriers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState(null);
  const [closing, setClosing] = useState(null);

  useEffect(() => {
    api.get("/containers/carriers").then((r) => setCarriers(r.data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const { data } = await api.get(`/eirs?${params.toString()}`);
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    if (!items.length) return;
    downloadCsv(items, `eirs_${new Date().toISOString().slice(0,10)}`, [
      { key: "eir_no", label: "EIR No" },
      { key: "container_number", label: "Container" },
      { key: "container_iso_type", label: "ISO" },
      { key: "carrier_code", label: "Carrier" },
      { key: "customer", label: "Customer" },
      { key: "status", label: "Status" },
      { key: "gate_in", label: "Gate In", format: (v) => v?.event_time?.slice(0, 19).replace("T", " ") },
      { key: "gate_in", label: "Driver IN", format: (v) => v?.driver_name },
      { key: "gate_in", label: "Truck IN", format: (v) => v?.truck_plate },
      { key: "gate_in", label: "Condition IN", format: (v) => v?.condition },
      { key: "gate_in", label: "Damages", format: (v) => v?.damages?.length ?? 0 },
      { key: "gate_out", label: "Gate Out", format: (v) => v?.event_time?.slice(0, 19).replace("T", " ") },
      { key: "gate_out", label: "Driver OUT", format: (v) => v?.driver_name },
      { key: "gate_out", label: "Truck OUT", format: (v) => v?.truck_plate },
      { key: "gate_out", label: "Released to", format: (v) => v?.released_to },
      { key: "closed_at", label: "Closed At" },
    ]);
  };

  if (selected) {
    return (
      <div data-testid="eirs-page">
        <PageHeader title="EIR Detail" subtitle={selected.eir_no} testid="eir-detail-header" />
        <div className="p-6 sm:p-8">
          <EirDetail
            eir={selected}
            onBack={() => setSelected(null)}
            onClose={() => setClosing(selected)}
          />
        </div>
        {closing && (
          <CloseEirDialog
            eir={closing}
            onClose={() => setClosing(null)}
            onClosed={async () => {
              setClosing(null);
              const { data } = await api.get(`/eirs/${selected.id}`);
              setSelected(data);
              load();
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div data-testid="eirs-page">
      <PageHeader
        title="EIRs"
        subtitle="Equipment Interchange Receipts — gate IN issuance, gate OUT closure, printable PDF."
        testid="eirs-header"
        actions={
          <>
            <button className="ps-btn ps-btn-secondary" onClick={load} data-testid="refresh-eirs">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <button className="ps-btn ps-btn-secondary" disabled={!items.length} onClick={exportCsv} data-testid="export-eirs-csv">
              <Download className="w-4 h-4" /> CSV
            </button>
            {canCreate && (
              <button className="ps-btn ps-btn-primary" onClick={() => setShowCreate(true)} data-testid="new-eir">
                <Plus className="w-4 h-4" /> New EIR
              </button>
            )}
          </>
        }
      />

      <div className="p-6 sm:p-8 space-y-4">
        {/* Filters */}
        <div className="border border-gray-200 bg-white p-4 grid sm:grid-cols-4 gap-3" data-testid="eir-filters">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
            <input
              placeholder="Container number"
              value={filters.container_number}
              onChange={(e) => setFilters({ ...filters, container_number: e.target.value.toUpperCase() })}
              className="ps-input font-mono pl-9"
              data-testid="filter-container"
            />
          </div>
          <select
            value={filters.carrier_code}
            onChange={(e) => setFilters({ ...filters, carrier_code: e.target.value })}
            className="ps-input font-mono"
            data-testid="filter-carrier"
          >
            <option value="">All carriers</option>
            {carriers.map((c) => (
              <option key={c.carrier} value={c.carrier}>{c.carrier} ({c.count})</option>
            ))}
          </select>
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="ps-input"
            data-testid="filter-status"
          >
            <option value="">All statuses</option>
            <option value="OPEN">Open</option>
            <option value="CLOSED">Closed</option>
          </select>
        </div>

        <div className="border border-gray-200 bg-white overflow-x-auto">
          <table className="ps-table" data-testid="eirs-table">
            <thead>
              <tr>
                <th>EIR No</th>
                <th>Container</th>
                <th>ISO</th>
                <th>Carrier</th>
                <th>Status</th>
                <th>Gate IN</th>
                <th>Driver IN</th>
                <th className="text-right">Damages</th>
                <th>Gate OUT</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={10} className="text-center text-gray-400 py-10">Loading…</td></tr>}
              {!loading && items.length === 0 && (
                <tr><td colSpan={10} className="text-center text-gray-400 py-10">No EIRs match the current filters. Click <strong>New EIR</strong> to issue one.</td></tr>
              )}
              {!loading && items.map((e) => (
                <tr key={e.id} data-testid={`eir-row-${e.eir_no}`}>
                  <td className="font-mono font-bold">{e.eir_no}</td>
                  <td className="font-mono">{e.container_number}</td>
                  <td className="font-mono">{e.container_iso_type}</td>
                  <td className="font-mono">{e.carrier_code || <span className="text-gray-400">—</span>}</td>
                  <td><StatusPill status={e.status} /></td>
                  <td className="text-xs text-gray-700 font-mono">{fmtTime(e.gate_in?.event_time)}</td>
                  <td className="text-xs">{e.gate_in?.driver_name || <span className="text-gray-400">—</span>}</td>
                  <td className="text-right font-mono font-bold">{e.gate_in?.damages?.length || 0}</td>
                  <td className="text-xs text-gray-700 font-mono">{e.gate_out ? fmtTime(e.gate_out.event_time) : <span className="text-gray-400">—</span>}</td>
                  <td className="text-right">
                    <div className="inline-flex gap-1">
                      <button
                        className="ps-btn ps-btn-secondary h-7 px-2 text-xs"
                        onClick={() => setSelected(e)}
                        data-testid={`view-eir-${e.eir_no}`}
                      ><Eye className="w-3.5 h-3.5" /> View</button>
                      <button
                        className="ps-btn ps-btn-secondary h-7 px-2 text-xs"
                        onClick={() => downloadEirPdf(e)}
                        data-testid={`print-eir-${e.eir_no}`}
                      ><Printer className="w-3.5 h-3.5" /> Print</button>
                      {e.status === "OPEN" && (
                        <button
                          className="ps-btn ps-btn-primary h-7 px-2 text-xs"
                          onClick={() => setClosing(e)}
                          data-testid={`close-eir-${e.eir_no}`}
                        ><Lock className="w-3.5 h-3.5" /> Close</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <CreateEirDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={() => load()}
      />
      {closing && (
        <CloseEirDialog
          eir={closing}
          onClose={() => setClosing(null)}
          onClosed={() => { setClosing(null); load(); }}
        />
      )}
    </div>
  );
}
